import { format } from "date-fns";
import { useEffect, useState } from "react";
import {
  AiOutlineClockCircle,
  AiOutlineLeft,
  AiOutlineMail,
  AiOutlineRight,
  AiOutlineUser,
} from "react-icons/ai";
import { FaBell } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Notification } from "../models/notification";
import { UserInfo } from "../models/user-info";
import notificationService from "../services/notification-service";

const statusClass = (status: string) => {
  switch (status) {
    case "sent":
    case "approved":
      return "bg-[#E6F6F3] text-green-800";
    case "rejected":
    case "revoked":
      return "bg-[#FEE2E2] text-red-800";
    default:
      return "bg-[#F3F4F6] text-gray-500";
  }
};

interface AcademicDashboardProps {
  userInfo: UserInfo;
}

export default function AcademicDashboard({
  userInfo,
}: AcademicDashboardProps) {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [paginationKeys, setPaginationKeys] = useState<string[]>([]); // Array to store pagination tokens
  const [currentPage, setCurrentPage] = useState(0); // Current page index
  const [pagination, setPagination] = useState<{
    limit: number;
    nextPageToken?: string;
  }>({
    limit: 10,
  });

  const getNotifications = async (
    status: string,
    search?: string,
    nextPageToken?: string,
  ) => {
    setLoading(true);
    const res = await notificationService.getUserNotifications({
      status,
      pagination: {
        limit: pagination.limit,
        nextPageToken,
      },
      search,
      returnRecipientCount: true,
    });
    if (!res) {
      setLoading(false);
      return;
    }

    setNotifications(res.items);

    // Update paginationKeys and currentPage
    if (nextPageToken) {
      setPaginationKeys((prev) => [...prev, nextPageToken]);
      setCurrentPage((prev) => prev + 1);
    }

    setPagination({
      limit: pagination.limit,
      nextPageToken: res.nextPageToken,
    });
    setLoading(false);
  };

  const filterByStatus = async (status: string) => {
    setStatusFilter(status);
    setPaginationKeys([]); // Reset pagination keys
    setCurrentPage(-1); // Reset current page
    await getNotifications(status);
  };

  const search = async (searchTerm: string) => {
    setSearchTerm(searchTerm);
    setPaginationKeys([]); // Reset pagination keys
    setCurrentPage(-1); // Reset current page
    await getNotifications(statusFilter, searchTerm);
  };

  useEffect(() => {
    getNotifications(statusFilter);
  }, []);

  const titleFormatted = (title: string, template: string = "") => {
    if (template) {
      return template
        ?.replace(/-/g, " ")
        ?.replace(/\b\w/g, (char) => char.toUpperCase());
    }

    return title?.substring(0, 40) || "No title";
  };

  return (
    <Layout
      userInfo={userInfo}
      hideNavigationLinks
      loading={loading}
      className="flex flex-col h-dvh bg-light"
    >
      <div className="flex items-center mx-4 my-4 gap-4">
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            navigate("/academic-compose-email");
          }}
          className="px-4 py-4 text-sm text-[#614175] bg-[#F7F4F9] mb-1 flex items-center gap-2 border-2 border-[#E2D3EC] rounded-lg w-64"
        >
          <span className="text-[#614175]">
            <AiOutlineMail className="w-4 h-4" />
          </span>
          {"Compose an Email"}
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            navigate("/import-email-recipients");
          }}
          className="px-4 py-4 text-sm text-[#41663E] bg-[#F3F8F2] mb-1 flex items-center gap-2 border-2 border-[#C6E0C3] rounded-lg w-64"
        >
          <span className="text-[#41663E]">
            <AiOutlineMail className="w-4 h-4" />
          </span>
          {"Set Automatic Email"}
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => {
            navigate("/send-notification");
          }}
          className="px-4 py-4 text-sm text-[#905E3A] bg-[#F7F4F9] mb-1 flex items-center gap-2 border-2 border-[#F7D9C4] rounded-lg w-64"
        >
          <span className="text-[#905E3A]">
            <FaBell className="w-4 h-4" />
          </span>
          {"Send a Notification"}
        </div>
      </div>
      <div className="flex items-center justify-between mx-4 mb-2 border-gray-300 border-b">
        <div className="flex gap-6">
          {[
            {
              label: "In Review",
              value: "pending",
            },
            {
              label: "Approved",
              value: "approved",
            },
            {
              label: "Sent",
              value: "sent",
            },
            // {
            //   label: "Revoked",
            //   value: "revoked",
            // },
            {
              label: "All",
              value: "",
            },
          ].map((status) => (
            <button
              key={status.value}
              onClick={() => filterByStatus(status.value)}
              className={`pb-2 text-sm font-bold transition border-b ${
                statusFilter === status.value
                  ? "border-red-600 text-red-600 border-b-2"
                  : "border-transparent text-gray-700 hover:text-red-600 hover:border-red-600 hover:border-b-2"
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
        <div className="flex items-center mb-4 w-[300px]">
          <div className="relative w-full">
            <input
              type="search"
              id="search"
              className="block p-2 w-full z-20 text-sm text-gray-900 bg-gray-50 rounded-e-lg border border-gray-300 outline-none focus:ring-0 focus:ring-offset-0 focus:ring-border focus:border-gray-300"
              placeholder="Search ..."
              onChange={async (e) => {
                const value = e.target.value;
                setSearchTerm(value);
              }}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  await search(searchTerm);
                }
              }}
            />
            <button
              type="submit"
              className="absolute top-0 end-0 p-2 text-sm font-medium h-full text-white order border-gray-700 hover:bg-gray-800 bg-gray-700 rounded-e-lg border focus:ring-0 focus:ring-offset-0 focus:ring-border focus:border-gray-300 "
              onClick={async () => {
                await search(searchTerm);
              }}
            >
              <svg
                className="w-4 h-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                />
              </svg>
              <span className="sr-only">Search</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex mx-4">
        <table className="w-full table-auto border-collapse text-sm">
          <tbody>
            {notifications.length === 0 && !loading && (
              <tr>
                <td className="p-0">
                  <div className="px-4 py-5 text-md text-gray-500 bg-white mb-1 flex items-center gap-4 justify-center">
                    No notifications found for the selected filter.
                  </div>
                </td>
              </tr>
            )}
            {notifications.map((notification) => (
              <tr key={notification.uuid}>
                <td className="p-0 ">
                  <div className="px-4 py-5 text-md text-gray-500 bg-white mb-1 flex items-center gap-4 rounded-l-lg">
                    <span className="text-[#00000040]">
                      {notification.PushNotification ? (
                        <FaBell className="w-4 h-4" />
                      ) : (
                        <AiOutlineMail className="w-4 h-4" />
                      )}
                    </span>
                    {titleFormatted(
                      notification.Title,
                      notification.EmailTemplate,
                    )}
                  </div>
                </td>
                <td className="p-0">
                  <div className="px-4 py-5 text-md text-gray-500 bg-white mb-1 flex items-center gap-4">
                    <span className="text-[#00000040]">
                      <AiOutlineClockCircle className="w-4 h-4" />
                    </span>
                    {notification.SendAt
                      ? format(
                          new Date(notification.SendAt),
                          "hh:mma   EEE,  dd MMM yyyy",
                        )
                      : "ASAP"}
                  </div>
                </td>
                <td className="p-0">
                  <div className="px-4 py-5 text-md text-gray-500 bg-white mb-1 flex items-center gap-4">
                    <span className="text-[#00000040]">
                      <AiOutlineUser className="w-4 h-4" />
                    </span>
                    {notification.RecipientCount}
                  </div>
                </td>
                <td className="p-0">
                  <div className="px-4 py-5 text-md text-gray-500 bg-white mb-1 flex items-center gap-4">
                    {notification.EmailNotification
                      ? notification.EmailTemplate
                      : "Push Notification"}
                  </div>
                </td>
                <td className="p-0">
                  <div className="px-4 py-5 text-md text-gray-500 bg-white mb-1 flex items-center gap-4 justify-end rounded-r-lg">
                    <span
                      className={`flex align-top items-center gap-1 p-1 text-xs rounded px-2 py-0.5
                            ${statusClass(notification.Status)}`}
                    >
                      <span className="uppercase">{notification.Status}</span>
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(paginationKeys.length || pagination.nextPageToken) && (
        <div className="flex gap-4 items-center justify-center w-full px-4 py-2 bg-light">
          <span
            className={`text-sm font-medium rounded-full p-1 ${
              currentPage > 0
                ? "cursor-pointer text-white bg-red-500"
                : "text-gray-500 bg-gray-100"
            }`}
            onClick={() => {
              if (currentPage > 0) {
                getNotifications(statusFilter, paginationKeys[currentPage - 1]);
                setCurrentPage((prev) => prev - 1);
              }
            }}
          >
            <AiOutlineLeft />
          </span>
          <span
            className={`text-sm font-medium rounded-full p-1 ${
              pagination.nextPageToken
                ? "cursor-pointer text-white bg-red-500"
                : "text-gray-500 bg-gray-100"
            }`}
            onClick={() => {
              if (pagination.nextPageToken) {
                getNotifications(statusFilter, pagination.nextPageToken);
              }
            }}
          >
            <AiOutlineRight />
          </span>
        </div>
      )}
    </Layout>
  );
}
